import { useLayoutEffect, useRef, useState, RefObject } from "react";

export interface TooltipPosition {
  top: number;
  left: number;
  /**
   * Pixel cap on the tooltip's rendered height; the consumer should pass this
   * to `maxHeight` so a tall tooltip in a small viewport (e.g. the Office
   * task pane) overflows internally rather than off the bottom of the screen.
   */
  maxHeight: number;
}

/**
 * Positions a portal-rendered tooltip relative to an anchor rect.
 * Prefers above the anchor, falls back below if it would clip the top edge,
 * and clamps horizontally to the viewport. Recomputes on viewport resize so
 * a window/task-pane resize doesn't leave the tooltip dangling off-screen.
 *
 * Returns a ref to attach to the tooltip root, the computed fixed-position
 * coordinates, and a maxHeight cap derived from the viewport.
 */
export function useTooltipPosition<T extends HTMLElement = HTMLDivElement>(
  anchorRect: DOMRect | null,
  isVisible: boolean
): { ref: RefObject<T>; position: TooltipPosition } {
  const ref = useRef<T>(null);
  const [position, setPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 16 : 9999,
  });

  // useLayoutEffect runs synchronously after DOM mutation, before paint, so
  // the corrected position is in place before the browser draws the first
  // frame. Avoids a one-frame flash at viewport top-left while the position
  // is computed.
  useLayoutEffect(() => {
    if (!isVisible || !anchorRect || !ref.current) return;

    const compute = () => {
      if (!ref.current) return;
      const tooltipRect = ref.current.getBoundingClientRect();
      const padding = 8;
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const maxHeight = viewportH - 2 * padding;

      // Height for placement math is the smaller of actual content and our cap.
      const effectiveHeight = Math.min(tooltipRect.height, maxHeight);

      let top = anchorRect.top - effectiveHeight - padding;
      let left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;

      if (top < padding) {
        top = anchorRect.bottom + padding;
      }
      // If below also overflows, clamp to viewport so the tooltip stays on-screen.
      if (top + effectiveHeight > viewportH - padding) {
        top = Math.max(padding, viewportH - effectiveHeight - padding);
      }

      if (left < padding) {
        left = padding;
      } else if (left + tooltipRect.width > viewportW - padding) {
        left = viewportW - tooltipRect.width - padding;
      }

      setPosition({ top, left, maxHeight });
    };

    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [isVisible, anchorRect]);

  return { ref, position };
}
